/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import Constants from '@/Constants';
import { useApiMutation, useApiQuery } from '@/hooks/useApi';
import {
  CheckPasswordResponse,
  GenericApiResponse,
  GetAtHomeAccessCodeResponse,
  GetDetailsResponse,
  GetMonsterCardsResponse,
  GetStudentCardsResponse,
  GetStudentMonsterCardsResponse,
  GetStudentSubjectCardsResponse,
  Grade,
  GradesResponse,
  LeaderboardResponse,
  makeRequest,
  Password,
  PostContentReportParameters,
  PostContentReportResponse,
  ResetPasswordResponse,
  ResetPasswordTokenResponse,
  SetStudentPasswordResponse,
  Student,
  StudentFeedbackParameters,
  StudentFeedbackResponse,
  StudentInfoResponse,
  StudentLoginResponse,
  StudentResponse,
} from '@/services/api';
import { createKey, getSessionItem } from '@/utils/helper';

const getCommonHeaders = () => ({
  'session-id': getSessionItem('sessionId'),
  'student-id': getSessionItem('studentId'),
});

const getPassword = (selectedFruit: string, selectedAnimal: string) => {
  return `${selectedFruit.toLowerCase()}_${selectedAnimal.toLowerCase()}`;
};

export const useGetGrades = () => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_GRADES_URL),
    queryFn: () => makeRequest<GradesResponse>(Constants.GET_GRADES_URL),
    enabled: !!localStorage.getItem('adminId'),
    select: (data) => data.data.grades,
  });
};

export const useGetStudents = (grade?: Grade) => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENTS_URL, { gradeId: grade?.id }),
    queryFn: () => makeRequest<StudentResponse>(Constants.GET_STUDENTS_URL, { gradeId: grade?.id }),
    enabled: !!grade && localStorage.getItem('accessLocation') === 'school',
    select: (data) => data?.data.students,
  });
};

export const useCheckPassword = (student?: Student, enabled?: boolean) => {
  return useApiQuery({
    queryKey: createKey(Constants.CHECK_PASSWORD_URL, { studentId: student?.id }),
    queryFn: () => {
      if (!student?.id) {
        throw new Error('studentId is missing');
      }
      return makeRequest<CheckPasswordResponse>(Constants.CHECK_PASSWORD_URL, {
        studentId: student.id,
      });
    },
    enabled: !!student?.id && enabled,
    select: (data) => data?.data.hasPassword,
    gcTime: 0,
    staleTime: 0,
  });
};
export const useStudentLogin = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<StudentLoginResponse>,
      unknown,
      { student: Student; password: Password },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.LOGIN_STUDENT_URL),
    mutationFn: ({ student, password }: { student: Student; password: Password }) =>
      makeRequest<StudentLoginResponse>(Constants.LOGIN_STUDENT_URL, {
        studentId: student?.id,
        password: getPassword(password.fruit, password.animal),
      }),
    retry: false,
    ...options,
  });
};

export const useStudentSetPassword = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<SetStudentPasswordResponse>,
      unknown,
      { student: Student; password: Password },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.SET_STUDENT_PASSWORD_URL),
    mutationFn: async ({ student, password }: { student: Student; password: Password }) => {
      const result = await makeRequest<SetStudentPasswordResponse>(
        Constants.SET_STUDENT_PASSWORD_URL,
        {
          studentId: student?.id,
          password: getPassword(password.fruit, password.animal),
        },
      );
      return result;
    },
    retry: false,
    ...options,
  });
};

export const useGetResetPasswordToken = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<ResetPasswordTokenResponse>,
      unknown,
      { student: Student; code: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.GET_RESET_PASSWORD_TOKEN_URL),
    mutationFn: ({ student, code }: { student: Student; code: string }) =>
      makeRequest<ResetPasswordTokenResponse>(Constants.GET_RESET_PASSWORD_TOKEN_URL, {
        studentCode: code,
        studentId: student?.id,
      }),
    retry: false,
    ...options,
  });
};

export const useResetPassword = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<ResetPasswordResponse>,
      unknown,
      { resetToken: string; password: Password; student: Student },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.RESET_PASSWORD_URL),
    mutationFn: ({
      student,
      password,
      resetToken,
    }: {
      student: Student;
      password: Password;
      resetToken: string;
    }) =>
      makeRequest<ResetPasswordResponse>(Constants.RESET_PASSWORD_URL, {
        resetPasswordToken: resetToken,
        password: getPassword(password.fruit, password.animal),
        studentId: student.id,
      }),
    retry: false,
    ...options,
  });
};

export const useGetStudentInfo = () => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_INFO),
    queryFn: () =>
      makeRequest<StudentInfoResponse>(Constants.GET_STUDENT_INFO, {}, getCommonHeaders(), 'get'),
    enabled: !!getSessionItem('studentId') && !!getSessionItem('sessionId'),
    select: (data) => data.data,
  });
};

export const useSubmitStudentFeedback = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<StudentFeedbackResponse>,
      unknown,
      StudentFeedbackParameters,
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.SUBMIT_STUDENT_FEEDBACK),
    mutationFn: ({
      category,
      description,
      adminId,
      appVersion,
      deviceId,
      platform,
      deviceMetadata,
    }: StudentFeedbackParameters) =>
      makeRequest<StudentFeedbackResponse>(
        Constants.SUBMIT_STUDENT_FEEDBACK,
        {
          category,
          description,
          adminId,
          appVersion,
          deviceId,
          platform,
          deviceMetadata,
        },
        getCommonHeaders(),
      ),
    retry: false,
    ...options,
  });
};

export const useGetStudentDetails = (moduleCompletionThreshold?: number | undefined) => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_DETAILS),
    queryFn: () => makeRequest<GetDetailsResponse>(Constants.GET_STUDENT_DETAILS, {}),
    enabled: !!localStorage.getItem('accessToken') && !moduleCompletionThreshold,
    select: (data) => data.data,
  });
};

export const useGetSubjectCards = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<GetStudentCardsResponse>,
      unknown,
      { subjectIdList: string[] },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.GET_STUDENT_CARDS),
    mutationFn: ({ subjectIdList }: { subjectIdList: string[] }) =>
      makeRequest<GetStudentCardsResponse>(
        Constants.GET_STUDENT_CARDS,
        {
          subjectIdList,
        },
        getCommonHeaders(),
      ),
    retry: false,
    ...options,
  });
};

export const useGetMonstersCards = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<GetMonsterCardsResponse>,
      unknown,
      { loIdList: string[] },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.GET_MONSTERS_CARDS),
    mutationFn: ({ loIdList }: { loIdList: string[] }) =>
      makeRequest<GetMonsterCardsResponse>(
        Constants.GET_MONSTERS_CARDS,
        {
          loIdList,
        },
        getCommonHeaders(),
      ),
    retry: false,
    ...options,
  });
};

export const useGetStudentMonsterCards = (enabled: boolean) => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_MONSTER_CARDS),
    queryFn: () =>
      makeRequest<GetStudentMonsterCardsResponse>(
        Constants.GET_STUDENT_MONSTER_CARDS,
        {},
        {
          ...getCommonHeaders(),
          'access-location': localStorage.getItem('accessLocation') || 'school',
        },
        'get',
      ),
    select: (data) => data.data,
    enabled,
    retry: false,
  });
};

export const useGetStudentSubjectCards = (enabled: boolean) => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_SUBJECT_CARDS),
    queryFn: () =>
      makeRequest<GetStudentSubjectCardsResponse>(
        Constants.GET_STUDENT_SUBJECT_CARDS,
        {},
        {
          ...getCommonHeaders(),
          'access-location': localStorage.getItem('accessLocation') || 'school',
        },
        'get',
      ),
    select: (data) => data.data,
    enabled,
    retry: false,
  });
};

export const useSubmitContentReport = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<PostContentReportResponse>,
      unknown,
      PostContentReportParameters,
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.CONTENT_REPORT_URL),
    mutationFn: ({
      programCode,
      studentCode,
      subjectName,
      gradeName,
      medium,
      moduleType,
      problemDescriptionTag,
      problemDescription,
      metadata,
    }: PostContentReportParameters) =>
      makeRequest<PostContentReportResponse>(
        Constants.CONTENT_REPORT_URL,
        {
          programCode,
          studentCode,
          subjectName,
          gradeName,
          medium,
          moduleType,
          problemDescriptionTag,
          problemDescription,
          metadata,
        },
        getCommonHeaders(),
      ),
    retry: false,
    ...options,
  });
};

export const useGetAtHomeAccessCode = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<GetAtHomeAccessCodeResponse>,
      unknown,
      { accessLocation: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.GET_AT_HOME_ACCESS_CODE_URL),
    mutationFn: ({ accessLocation }: { accessLocation: string }) =>
      makeRequest<GetAtHomeAccessCodeResponse>(
        Constants.GET_AT_HOME_ACCESS_CODE_URL,
        {},
        getCommonHeaders(),
        'get',
      ),
    retry: false,
    ...options,
  });
};

export const useGetLeaderboard = (enabled: boolean) => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_LEADERBOARD_URL),
    queryFn: () =>
      makeRequest<LeaderboardResponse>(
        Constants.GET_LEADERBOARD_URL,
        {},
        getCommonHeaders(),
        'get',
      ),
    select: (data) => data.data,
    enabled,
  });
};
